import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import StatusBadge from '@/components/common/StatusBadge';
import { getBuildingById, getFloorById, getRoomsByFloor } from '@/services/nocodeBackendService';

const { FiMonitor, FiArrowLeft } = FiIcons;

const FloorDetails = () => {
  const { buildingId, floorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ building: null, floor: null, rooms: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getBuildingById(buildingId),
      getFloorById(floorId),
      getRoomsByFloor(floorId)
    ])
    .then(([bData, fData, rData]) => {
      setData({ building: bData, floor: fData, rooms: rData });
    })
    .catch(() => navigate('/accommodation'))
    .finally(() => setLoading(false));
  }, [buildingId, floorId, navigate]);

  if (loading) return <div className="p-12 text-center text-slate-500">Loading floor details...</div>;
  if (!data.floor) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[
        { label: 'Accommodation', path: '/accommodation' },
        { label: data.building.name, path: `/accommodation/${buildingId}` },
        { label: data.floor.floor_name }
      ]} />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600 transition-colors">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          {data.floor.floor_name} Rooms
        </h1>
        <p className="text-slate-600 mt-2 ml-10">Select a room to view individual bed-space availability.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {data.rooms.map(room => (
          <Link 
            key={room.id} 
            to={`/accommodation/${buildingId}/floor/${floorId}/room/${room.id}`}
            className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-blue-300 transition-all"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="bg-slate-100 p-2.5 rounded-lg text-slate-600">
                <SafeIcon icon={FiMonitor} className="w-5 h-5" />
              </div>
              <StatusBadge status={room.status} />
            </div>
            <h3 className="font-bold text-lg text-slate-900">{room.room_name}</h3>
            {room.is_demo && (
              <span className="inline-block mt-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Demo Room</span>
            )}
          </Link>
        ))}
      </div>
      
      {data.rooms.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 text-slate-500">
          No rooms have been added to this floor yet.
        </div>
      )}
    </div>
  );
};

export default FloorDetails;